import { useState } from 'react';
import { cities } from '../data/citiesData';
import { arnonaRates, calcTotalPurchaseCost } from '../data/taxData';
import { formatILS, formatPercent } from '../utils/format';

export default function NetCalculator() {
  const [cityId, setCityId] = useState('');
  const [price, setPrice] = useState('');
  const [surface, setSurface] = useState('');
  const [monthlyRent, setMonthlyRent] = useState('');
  const [vaadBayit, setVaadBayit] = useState('400');
  const [managementFee, setManagementFee] = useState('8');
  const [taxRate, setTaxRate] = useState('15');
  const [maintenanceRate, setMaintenanceRate] = useState('0.3');
  const [insuranceAnnual, setInsuranceAnnual] = useState('2400');
  const [arnonaPaidBy, setArnonaPaidBy] = useState('tenant'); // 'tenant' | 'owner'
  const [result, setResult] = useState(null);

  const handleCityChange = (e) => {
    setCityId(e.target.value);
    const city = cities.find(c => c.id === e.target.value);
    if (city) {
      setPrice(city.rentalYields[1].averagePrice);
      setSurface('55');
      setMonthlyRent(city.rentalYields[1].averageMonthlyRent);
    }
  };

  const calculate = () => {
    const p = parseFloat(price);
    const surf = parseFloat(surface);
    const rent = parseFloat(monthlyRent);
    const vaad = parseFloat(vaadBayit);
    const mgmt = parseFloat(managementFee);
    const tax = parseFloat(taxRate);
    const maintRate = parseFloat(maintenanceRate);
    const insurance = parseFloat(insuranceAnnual);
    if (!p || !rent) return;

    const annualRent = rent * 12;
    const grossYield = (annualRent / p) * 100;

    // Purchase costs
    const purchaseCost = calcTotalPurchaseCost(p);

    // Annual charges
    const arnonaAnnual = (arnonaPaidBy === 'owner' && surf) ? (arnonaRates[cityId] || 100) * surf : 0;
    const vaadAnnual = vaad * 12;
    const mgmtAnnual = annualRent * (mgmt / 100);
    const maintenanceAnnual = p * (maintRate / 100);

    const totalCharges = arnonaAnnual + vaadAnnual + mgmtAnnual + maintenanceAnnual + insurance;

    // Net revenue before tax
    const netBeforeTax = annualRent - totalCharges;

    // Tax
    const rentalTax = netBeforeTax > 0 ? netBeforeTax * (tax / 100) : 0;
    const netAfterTax = netBeforeTax - rentalTax;

    // Net yield on purchase price
    const netYield = (netAfterTax / p) * 100;
    // Net yield on total invested (incl. purchase costs)
    const totalInvested = p + purchaseCost.total;
    const netYieldOnTotal = (netAfterTax / totalInvested) * 100;

    // Monthly cashflow
    const monthlyCashflow = netAfterTax / 12;

    setResult({
      annualRent, grossYield, purchaseCost, totalInvested,
      arnonaAnnual, vaadAnnual, mgmtAnnual, maintenanceAnnual, insuranceAnnual: insurance,
      totalCharges, netBeforeTax, rentalTax, netAfterTax,
      netYield, netYieldOnTotal, monthlyCashflow, maintRate, tax,
    });
  };

  const blockStyle = { background: 'var(--color-bg-white)', borderRadius: 'var(--radius-lg)', padding: '2rem', border: '1px solid var(--color-border)', marginBottom: '1.5rem' };

  return (
    <div className="page-content">
      <div className="container" style={{ maxWidth: 960 }}>
        <span className="gold-tag">Outil avancé</span>
        <h1>Calculateur de rendement net</h1>
        <p style={{ fontSize: '1.05rem', marginBottom: '2.5rem' }}>
          Calculez votre rendement réel après toutes les charges et taxes israéliennes : Arnona, Va'ad Bayit, frais de gestion, maintenance, impôt sur les loyers et frais d'acquisition.
        </p>

        <div className="grid-2" style={{ alignItems: 'start' }}>
          {/* Formulaire */}
          <div>
            <div style={blockStyle}>
              <h3 className="card-title">Le bien</h3>
              <div className="form-group">
                <label className="form-label">Ville (charge les valeurs par défaut)</label>
                <select className="form-select" value={cityId} onChange={handleCityChange}>
                  <option value="">-- Choisir une ville --</option>
                  {cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Prix d'achat (₪)</label>
                  <input className="form-input" type="number" value={price} onChange={e => setPrice(e.target.value)} placeholder="1 500 000" />
                </div>
                <div className="form-group">
                  <label className="form-label">Surface (m²)</label>
                  <input className="form-input" type="number" value={surface} onChange={e => setSurface(e.target.value)} placeholder="60" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Loyer mensuel (₪)</label>
                <input className="form-input" type="number" value={monthlyRent} onChange={e => setMonthlyRent(e.target.value)} placeholder="5 000" />
              </div>
            </div>

            <div style={blockStyle}>
              <h3 className="card-title">Charges annuelles</h3>

              {/* Arnona toggle */}
              <div className="form-group">
                <label className="form-label" style={{ marginBottom: '0.5rem' }}>
                  Arnona (taxe municipale)
                  <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', fontWeight: 400, marginLeft: '0.5rem' }}>
                    — normalement payée par le locataire
                  </span>
                </label>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button
                    type="button"
                    onClick={() => setArnonaPaidBy('tenant')}
                    style={{
                      padding: '0.45rem 1rem',
                      borderRadius: 'var(--radius-sm)',
                      border: `1.5px solid ${arnonaPaidBy === 'tenant' ? 'var(--color-success)' : 'var(--color-border)'}`,
                      background: arnonaPaidBy === 'tenant' ? 'rgba(34,197,94,0.08)' : 'transparent',
                      color: arnonaPaidBy === 'tenant' ? 'var(--color-success)' : 'var(--color-text-muted)',
                      fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'var(--font-body)',
                    }}>
                    Locataire (défaut)
                  </button>
                  <button
                    type="button"
                    onClick={() => setArnonaPaidBy('owner')}
                    style={{
                      padding: '0.45rem 1rem',
                      borderRadius: 'var(--radius-sm)',
                      border: `1.5px solid ${arnonaPaidBy === 'owner' ? 'var(--color-danger)' : 'var(--color-border)'}`,
                      background: arnonaPaidBy === 'owner' ? 'rgba(239,68,68,0.08)' : 'transparent',
                      color: arnonaPaidBy === 'owner' ? 'var(--color-danger)' : 'var(--color-text-muted)',
                      fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'var(--font-body)',
                    }}>
                    Propriétaire (vacant / saisonnier)
                  </button>
                </div>
                {arnonaPaidBy === 'owner' && (
                  <div style={{ marginTop: '0.4rem', fontSize: '0.78rem', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                    L'Arnona sera déduite des revenus (calculée selon la surface et la ville).
                  </div>
                )}
              </div>

              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">
                    Va'ad Bayit (₪/mois)
                    <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', fontWeight: 400, display: 'block' }}>
                      Charges copropriété — payé par le propriétaire
                    </span>
                  </label>
                  <input className="form-input" type="number" value={vaadBayit} onChange={e => setVaadBayit(e.target.value)} placeholder="400" />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Gestion locative (%)
                    <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', fontWeight: 400, display: 'block' }}>
                      Si agence — sur loyer brut
                    </span>
                  </label>
                  <input className="form-input" type="number" value={managementFee} onChange={e => setManagementFee(e.target.value)} placeholder="8" />
                </div>
              </div>

              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">
                    Maintenance (% du prix)
                    <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', fontWeight: 400, display: 'block' }}>
                      Entretien courant — payé par le propriétaire
                    </span>
                  </label>
                  <input className="form-input" type="number" step="0.1" value={maintenanceRate} onChange={e => setMaintenanceRate(e.target.value)} placeholder="0.3" />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Assurance (₪/an)
                    <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', fontWeight: 400, display: 'block' }}>
                      Assurance propriétaire non-occupant
                    </span>
                  </label>
                  <input className="form-input" type="number" value={insuranceAnnual} onChange={e => setInsuranceAnnual(e.target.value)} placeholder="2400" />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Régime fiscal</label>
                <select className="form-select" value={taxRate} onChange={e => setTaxRate(e.target.value)}>
                  <option value="15">Forfait 15 % (sur revenu brut)</option>
                  <option value="25">Taux réel ~25 %</option>
                  <option value="35">Taux marginal ~35 %</option>
                  <option value="0">Exonéré / Non imposable</option>
                </select>
              </div>
            </div>

            <button className="btn btn-primary btn-lg" onClick={calculate} style={{ width: '100%', justifyContent: 'center' }}>
              Calculer le rendement net
            </button>
          </div>

          {/* Résultats */}
          <div>
            {result ? (
              <>
                {/* 3 résultats clés */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div className="result-box">
                    <span className="result-value">{formatPercent(result.grossYield)}</span>
                    <span className="result-label">Rendement brut</span>
                  </div>
                  <div className="result-box" style={{ background: 'linear-gradient(135deg, #0f3320, #14532d)' }}>
                    <span className="result-value">{formatPercent(result.netYield)}</span>
                    <span className="result-label">Rendement net (sur prix d'achat)</span>
                  </div>
                </div>

                {/* Cashflow mensuel — mis en avant */}
                <div style={{ ...blockStyle, marginBottom: '1rem', background: result.monthlyCashflow >= 0 ? 'rgba(34,197,94,0.06)' : 'rgba(239,68,68,0.06)', borderLeft: `3px solid ${result.monthlyCashflow >= 0 ? 'var(--color-success)' : 'var(--color-danger)'}` }}>
                  <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: result.monthlyCashflow >= 0 ? 'var(--color-success)' : 'var(--color-danger)', marginBottom: 4 }}>
                    Cashflow mensuel net
                  </div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: 700, color: result.monthlyCashflow >= 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>
                    {result.monthlyCashflow >= 0 ? '+' : ''}{formatILS(Math.round(result.monthlyCashflow), true)}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginTop: 4 }}>
                    Revenu annuel net après impôt / 12
                  </div>
                </div>

                {/* Net sur investissement total */}
                <div style={{ ...blockStyle, marginBottom: '1rem', borderLeft: '3px solid var(--color-accent)', background: 'rgba(196,163,90,0.05)' }}>
                  <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--color-accent)', marginBottom: 6 }}>Capital total investi</div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', fontWeight: 700, color: 'var(--color-primary)' }}>{formatILS(result.totalInvested, true)}</div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', marginTop: 4 }}>
                    Prix + frais d'acquisition — Rendement net sur capital total : <strong>{formatPercent(result.netYieldOnTotal)}</strong>
                  </div>
                </div>

                {/* Détail charges */}
                <div style={blockStyle}>
                  <h3 className="card-title">Détail annuel</h3>
                  <table style={{ width: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
                    <tbody>
                      {[
                        { label: 'Loyer annuel brut', value: result.annualRent, color: 'var(--color-success)' },
                        result.arnonaAnnual > 0
                          ? { label: `Arnona (${surface || '–'} m² — propriétaire)`, value: -result.arnonaAnnual }
                          : { label: `Arnona (payée par le locataire — non déduite)`, value: 0, neutral: true },
                        { label: "Va'ad Bayit (charges copropriété)", value: -result.vaadAnnual },
                        { label: 'Gestion locative', value: -result.mgmtAnnual },
                        { label: `Maintenance (${result.maintRate} % du prix)`, value: -result.maintenanceAnnual },
                        { label: 'Assurance propriétaire', value: -result.insuranceAnnual },
                        { label: 'Revenu net avant impôt', value: result.netBeforeTax, bold: true },
                        { label: `Impôt sur revenus locatifs (${result.tax} %)`, value: -result.rentalTax },
                        { label: 'Revenu net après impôt', value: result.netAfterTax, bold: true, color: 'var(--color-primary)' },
                      ].map((row, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid var(--color-border)' }}>
                          <td style={{ padding: '0.5rem 0', color: row.neutral ? 'var(--color-text-muted)' : row.bold ? 'var(--color-text)' : 'var(--color-text-muted)', fontWeight: row.bold ? 700 : 400, fontStyle: row.neutral ? 'italic' : 'normal' }}>{row.label}</td>
                          <td style={{ padding: '0.5rem 0', textAlign: 'right', fontWeight: row.bold ? 700 : 500, color: row.neutral ? 'var(--color-text-muted)' : row.color || (row.value < 0 ? 'var(--color-danger)' : 'inherit') }}>
                            {row.neutral ? '—' : (row.value >= 0 ? '+' : '') + formatILS(row.value, true)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Frais d'acquisition */}
                <div style={blockStyle}>
                  <h3 className="card-title">Frais d'acquisition</h3>
                  <table style={{ width: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
                    <tbody>
                      {[
                        { label: "Taxe d'achat (Mas Rechisha)", value: result.purchaseCost.tax },
                        { label: 'Honoraires avocat (1,25 %)', value: result.purchaseCost.lawyer },
                        { label: 'Commission agence (2 %)', value: result.purchaseCost.agency },
                        { label: "Frais d'enregistrement Tabu", value: result.purchaseCost.registration },
                        { label: "Total frais d'acquisition", value: result.purchaseCost.total, bold: true },
                      ].map((row, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid var(--color-border)' }}>
                          <td style={{ padding: '0.5rem 0', color: row.bold ? 'var(--color-text)' : 'var(--color-text-muted)', fontWeight: row.bold ? 700 : 400 }}>{row.label}</td>
                          <td style={{ padding: '0.5rem 0', textAlign: 'right', fontWeight: row.bold ? 700 : 500 }}>{formatILS(row.value, true)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div style={{ ...blockStyle, textAlign: 'center', padding: '4rem 2rem', color: 'var(--color-text-muted)' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '3rem', color: 'var(--color-border)', marginBottom: '1rem' }}>—</div>
                <p style={{ margin: 0 }}>Remplissez le formulaire et cliquez sur "Calculer" pour voir le détail de votre rendement net.</p>
              </div>
            )}
          </div>
        </div>

        <div className="insight-box" style={{ marginTop: '2rem' }}>
          Ce calculateur est indicatif. Les taux d'Arnona, les frais d'acquisition et la fiscalité peuvent varier. Consultez un avocat fiscaliste israélien pour une simulation personnalisée et juridiquement opposable.
        </div>
      </div>
    </div>
  );
}
